import { HowToScanBlock } from "./HowToScanBlock";
import { VideoPlayer } from "./VideoPlayer";

import React, { useEffect, useState } from "react";

import { CountdownTimer } from "components/UI/CountdownTimer";
import { DrawerPopup } from "components/UI/DrawerPopup";

import { useTransferCertificate } from "hooks/api/useTransferCertificate";
import type { CertificateCustomer } from "types";

import { Button, Box, Typography } from "@mui/material";
import { useGetCertificatePublic } from "hooks/api/useGetCertificatePublic";
import { useQueryClient } from "@tanstack/react-query";

interface ClaimScanProcessProps {
  certificate: CertificateCustomer;
  title?: string;
  closeTransfer: () => void;
  onExpiredHandler?: () => void;
}

const REACT_APP_API_URL = import.meta.env.REACT_APP_API_URL;

export const ClaimScanProcess = ({
  certificate,
  onExpiredHandler,
  title = "Hold your phone near your item to scan",
}: ClaimScanProcessProps) => {
  const queryClient = useQueryClient();
  const { cancelTransfer, confirmCancelRequestTransfer, isStatusChanged } =
    useTransferCertificate();

  const [isOpenHowToScan, setIsOpenHowToScan] = useState<boolean>(false);
  const [isOpenVideoGuide, setIsOpenVideoGuide] = useState<boolean>(false);

  const { data: certificateData } = useGetCertificatePublic(certificate.id);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['certificate-public', certificate.id] });
  }, [isStatusChanged]);

  const cancelTransferHandler = () => {
    cancelTransfer({ certificateId: certificate.id });
  };

  const confirmCancelRequestTransferHandle = () => {
    confirmCancelRequestTransfer({ certificateId: certificate.id });
  };

  const handleOpenVideoGuide = () => {
    setIsOpenHowToScan(false);
    setIsOpenVideoGuide(true);
  };

  /**
   * VRG-286: the path segment is the CHIP UID. The route is positional
   * (`/scan/session/:id`), so no URL SHAPE changed — but the VALUE must remain the chip UID.
   * Passing `certificate.tag_id` (now our uuid FK) here would open a session that resolves
   * to nothing.
   */
  const getCertificateScanUrl = async (tagUid: string) => {
    return `${REACT_APP_API_URL}/scan/session/${tagUid}`;
  }

  const scanCertificateHandler = async () => {
    try {
      // get the scan token from the server
      //
      // VRG-231 dropped the `?? certificate.e_tag_id` fallback that used to follow — it was an
      // alias carrying the identical value. `tag_uid` STAYS on the payload partly for this
      // call: the sender who reaches this screen is still the certificate's OWNER (ownership
      // moves at accept), so they are served the full projection, which carries the UID.
      //
      // The guard is new and is not defensive padding. The removed alias was typed
      // non-optional, so it was silently satisfying the `string` parameter for every caller;
      // without it the absent case is visible for the first time. A certificate with no chip
      // bound has no scan session to open, and building the URL anyway produced
      // `/scan/session/undefined` — a request that 404s after the tab has already opened.
      if (!certificate.tag_uid) {
        console.error('Cannot start a scan session: this certificate has no tag bound.');
        return;
      }

      const scanUrl = await getCertificateScanUrl(certificate.tag_uid);

      // Open scan URL in new window
      window.open(scanUrl, "_blank");
    } catch (error) {
      console.error('Error initiating scan:', error);
      // Here you might want to show an error message to the user
    }
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <>
        <Typography px={1} variant="body1">
          {title}
        </Typography>
        <Box my="46px" sx={{ textAlign: "center" }}>
          <svg width={160} height={160}>
            <use href="#scanItem" />
          </svg>
        </Box>
        <Button
          sx={(theme) => ({
            marginBottom: "16px",
          })}
          onClick={scanCertificateHandler}
          fullWidth
        >
          Scan Item
        </Button>

        {certificateData?.transfer?.expire_at ? (
          <Button
            onClick={cancelTransferHandler}
            fullWidth
            variant="outlined"
          >
            Cancel Transfer in
            <CountdownTimer
              expiredAt={certificateData.transfer?.expire_at}
              timeIsOut={onExpiredHandler}
            />
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={confirmCancelRequestTransferHandle}
            fullWidth
          >
            Cancel Transfer
          </Button>
        )}

        <Box
          onClick={() => setIsOpenHowToScan(true)}
          sx={{ textAlign: "center", marginTop: "24px" }}
        >
          <p className="text-primary text-decoration-underline">How to scan?</p>
        </Box>
      </>

      {isOpenHowToScan && (
        <DrawerPopup
          title="How to scan?"
          child={
            <HowToScanBlock
              isTransferring
              onAcceptTransfer={() => setIsOpenHowToScan(false)}
              onWatchVideo={handleOpenVideoGuide}
            />
          }
          handleOpen={isOpenHowToScan}
          handleClose={() => setIsOpenHowToScan(false)}
        />
      )}

      {isOpenVideoGuide && (
        <VideoPlayer handleClose={() => setIsOpenVideoGuide(false)} />
      )}
    </Box>
  );
};
